import {
  nextTick,
  toRefs,
} from 'vue';
import {
  createWebHistory,
  createRouter,
  RouteLocationNormalizedGeneric,
} from 'vue-router';
import useStore from '@learner/store';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import TSplashScreenModal from '@components/SplashScreen/SplashScreenModal';
import { LEARNER_ROUTES } from './route-names';
import routes from './routes';

const DEFAULT_TITLE = 'Traliant';

// create the router object
const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_PUBLIC_PATH),
  routes,
});

const toggleBodyClass = (
  to: RouteLocationNormalizedGeneric,
  className: string,
  command?: 'add' | 'remove',
) => {
  if (to.name !== LEARNER_ROUTES.login) return;
  const $body = document.querySelector('body');
  if (!$body) { return; }

  if (command && ['add', 'remove'].includes(command)) {
    $body.classList[command](className);
  } else {
    $body.classList.toggle(className);
  }
};

/**
 * Navigation guard for authentication and lobby handling
 *
 * - For authenticated routes (requiresAuth): ensures user is logged in, redirects to login if not
 * - For lobby routes (login page, etc.): redirects authenticated users to home
 */
router.beforeEach(async (to) => {
  const { requiresAuth } = to.meta;
  const lobby = to.matched.some((record) => record.meta.lobby);
  const loginPath = { name: LEARNER_ROUTES.login, query: { redirect: to.fullPath } };
  const rootPath = { name: LEARNER_ROUTES.home, query: { redirect: to.fullPath } };

  toggleBodyClass(to, 'has-blue-bg', 'add');

  if (requiresAuth && typeof to.meta.bypassIf === 'function') {
    return to.meta.bypassIf(to);
  }

  const $store = useStore();
  const { user } = toRefs($store);

  const closeModal = (modal: any = null) => {
    if (modal) {
      modal.close();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initUser = async (modal: any = null): Promise<boolean> => {
    try {
      await $store.fetchAuthUser();
      closeModal(modal);
      return !!user.value;
    } catch {
      closeModal(modal);
      return false;
    }
  };

  // Handle authenticated routes - redirect to login if not authenticated
  if (requiresAuth && !user.value) {
    if (to.meta.showSplash !== false) {
      return new Promise((resolve) => {
        TSplashScreenModal({
          onAfterEnter: async (modal: any) => {
            const isAuthenticated = await initUser(modal);
            resolve(isAuthenticated ? true : loginPath);
          },
        });
      });
    }

    const isAuthenticated = await initUser();
    return isAuthenticated ? true : loginPath;
  }

  // Handle lobby routes - redirect authenticated users to home
  if (lobby && !user.value) {
    try {
      await $store.fetchAuthUser();
      if (user.value) {
        return rootPath;
      }
    } catch {
      // Allow navigation on error
    }
  }

  return true;
});

// set the page title
router.afterEach((to) => {
  toggleBodyClass(to, 'has-blue-bg', 'remove');
  // Use next tick to handle router history correctly

  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  nextTick(() => {
    document.title = to?.meta?.title ? `Traliant - ${to.meta.title}` : DEFAULT_TITLE;
  });
});

export default router;
